import { api, recordPageView, NotifyModelKey } from "./api";
import { getElement, PageStatus, normalizeForSearch, attachDropdownKeyboardNav, renderSortMenu, SUBJECT_QUICK_ADD_ENABLED, ADD_REVIEW_MODAL_ENABLED, hasAlreadyReviewedThisPost, markPostAsReviewed, EXAM_RATING_LABELS, renderEaseOfPassingText } from "./generics";
import {
    Comment, CommentSortField, Subject, FacultyCandidate, renderComment, sortComments, groupCommentsBySubject,
    initCommentForm, Votetype, GENERAL_SUBJECT_LABEL, sortSubjectsByRecentActivity, renderWpiaWikiSourceBadge,
} from "./comments";
import { createCompareButton, renderRatingSummary, renderWouldTakeAgainBadge, showPartialStars, renderRatingDistribution, Post } from "./posts";
import { ExamQuestion, renderExamQuestion, initExamQuestionForm } from "./examQuestions";
import { renderPostHocNotifyPrompt } from "./notifyEmail";
import { openAddPersonDialog } from "./addLecturer";

// Podział zajęcia/egzamin (patrz supports_exam_dimensions w views.post_detail) - na razie tylko
// WPiA UW. "zajecia" = dzisiejsze zachowanie bez zmian (wszystkie/prawie wszystkie komentarze -
// patrz filterCommentsByDimension), "egzamin" = tylko opinie z wypełnionym dowolnym wymiarem egzaminu,
// "pytania" = zupełnie inna treść (ExamQuestion, nie Comment) - lista pytań + osobny formularz.
type DimensionTab = "zajecia" | "egzamin" | "pytania";

// Constants
const commentList   = getElement<HTMLDivElement>("comment-list");
const commentForm   = getElement<HTMLDivElement>("comment-form");
const ratingSummaryContainer = getElement<HTMLDivElement>("rating-summary");
const compareButtonContainer = getElement<HTMLDivElement>("compare-button-container");
const addReviewBtn  = getElement<HTMLButtonElement>("add-review-btn");
const addReviewHint = getElement<HTMLSpanElement>("add-review-hint");
const addExamQuestionBtn = document.getElementById("add-exam-question-btn") as HTMLButtonElement | null;
// Ustawiane niżej, TYLKO gdy ADD_REVIEW_MODAL_ENABLED (patrz generics.ts) - formularz opinii
// (#comment-form) jest wtedy przenoszony do środka natywnego <dialog> zamiast rozwijać się
// inline. Referencja potrzebna zarówno w listenerze kliknięcia przycisku "Dodaj opinię" (otwiera
// dialog), jak i w onAutoClose przekazywanym do initCommentForm (zamyka dialog po udanym
// wysłaniu opinii) - stąd `let` w zasięgu modułu, przypisywane raz przy starcie strony.
let addReviewDialog: HTMLDialogElement | null = null;

// Zamyka dialog opinii, ODEJMUJĄC najpierw fokus z jego wnętrza - natywny <dialog>.close()
// (wołany niżej ze wszystkich miejsc zamykających) PRZYWRACA fokus elementowi, który miał go
// PRZED showModal() (czyli addReviewBtn - patrz listener kliknięcia niżej), ale TYLKO gdy fokus w
// chwili zamykania wciąż jest gdzieś w środku dialogu. Bez tego blur() zamknięcie po udanym
// wysłaniu opinii (fokus wciąż na submitBtn formularza - patrz onAutoClose przekazywane do
// initCommentForm) programowo oddawało fokus z powrotem na addReviewBtn, co odpalało jego własny
// listener "focus" niżej - a ten, skoro postAlreadyReviewed() jest już true (patrz
// markPostAsReviewed w onSubmitted), od razu pokazywał podpowiedź limitu ("Możesz dodać tylko
// jedną opinię...") SAMA Z SIEBIE, kilka sekund po wysłaniu, bez żadnej akcji użytkownika
// (zgłoszenie: "samo się klika w dodano opinię i pokazuje komunikat (samo)"). Blur elementu, który
// akurat ma fokus w środku dialogu, sprawia, że w chwili close() fokus nie jest już potomkiem
// dialogu - krok przywracania fokusu w ogóle się wtedy nie wykonuje.
// Wołane z przycisku "Dodaj opinię" i z onReopen (formularz otwiera okienko z powrotem, gdy po
// optymistycznym zamknięciu AI zwróciło podpowiedź albo opinia czeka na moderację).
function openAddReviewDialog(): void {
    if (!addReviewDialog || addReviewDialog.open) return;
    addReviewDialog.showModal();
    addReviewBtn.setAttribute("aria-expanded", "true");
    addReviewBtn.classList.add("active");
}

function closeAddReviewDialog(): void {
    if (!addReviewDialog) return;
    const active = document.activeElement as HTMLElement | null;
    if (active && addReviewDialog.contains(active)) {
        active.blur();
    }
    addReviewDialog.close();
}
const sortMenuContainer = getElement<HTMLDivElement>("sort-menu-container");
const statusBar     = getElement<HTMLParagraphElement>("status-bar");
const examQuestionsArea = document.getElementById("exam-questions-area") as HTMLDivElement | null;
const dimensionTabContainer = document.getElementById("dimension-tab-container") as HTMLDivElement | null;
const COMMENT_SORT_OPTIONS: { value: CommentSortField, label: string }[] = [
    { value: "newest", label: "Najnowsze" },
    { value: "by_subject", label: "Wg przedmiotu" },
    { value: "most_upvotes", label: "Najwięcej polubień" },
    { value: "oldest", label: "Najstarsze" },
    { value: "best_rating", label: "Najwyższa ocena" },
    { value: "worst_rating", label: "Najniższa ocena" },
];
const postSearchInfo = getElement<HTMLDivElement>("post-search-info");
const postSearchWrapper = getElement<HTMLDivElement>("search-with-suggestions");
const postSearchForm = getElement<HTMLFormElement>("post-search-form");
const postSearchInput = getElement<HTMLInputElement>("search-input");
const postSearchSuggestions = getElement<HTMLDivElement>("post-search-suggestions");

let searchCandidates: Post[] = [];


function loadSavedVotes(): { [commentId: number]: Votetype } {
    try {
        const saved = localStorage.getItem("comments_up_down_votes");
        return saved ? JSON.parse(saved) : {};
    } catch (e) {
        console.error("Błąd odczytu z localStorage", e);
        return {};
    }
}

function saveVotesToStorage(votes: { [commentId: number]: Votetype }) {
    try {
        localStorage.setItem("comments_up_down_votes", JSON.stringify(votes));
    } catch (e) {
        console.error("Błąd zapisu do localStorage", e);
    }
}

// "main" = główny formularz na górze strony (dowolny temat); "general" = sekcja "Komentarz
// ogólny"; number = id przedmiotu. Tylko jeden formularz dodawania komentarza jest otwarty naraz.
type CommentTarget = "main" | "general" | number;

const COMMENT_SORT_STORAGE_KEY = "mumin_comment_sort";
const VALID_COMMENT_SORTS: CommentSortField[] = [
    "by_subject", "most_upvotes", "newest", "oldest", "best_rating", "worst_rating",
];

function loadSavedCommentSort(): CommentSortField {
    try {
        const saved = localStorage.getItem(COMMENT_SORT_STORAGE_KEY);
        if (saved && (VALID_COMMENT_SORTS as string[]).includes(saved)) {
            return saved as CommentSortField;
        }
    } catch { /* ignore */ }
    return "newest";
}

function saveCommentSort(sortBy: CommentSortField): void {
    try {
        localStorage.setItem(COMMENT_SORT_STORAGE_KEY, sortBy);
    } catch { /* ignore */ }
}

let state: AppState = {
    comments: [],
    status: "idle",
    error: "",
    sortBy: loadSavedCommentSort(),
    focusedIndex: -1,
    post_id : null,
    comments_up_down_votes : loadSavedVotes(),
    visibleCount: 10,
    subjects: [],
    subjectsLoaded: false,
    activeCommentTarget: "main",
    collapsedTargets: new Set(),
};

// Główny formularz jest budowany tylko raz (gdy przedmioty/lata się załadują) - kolejne
// rendery go nie dotykają, żeby nie kasować tego, co użytkownik akurat wpisuje (np. przy
// doładowaniu kolejnych komentarzy przy przewijaniu w trybie płaskim).
let mainFormRendered = false;
// Handle zwrócony przez initCommentForm (patrz comments.ts) - jedyny sposób, żeby formularz
// zbudowany RAZ (wyżej) mimo to reagował na zakładkę (Zajęcia/Egzamin), na której user stał, gdy
// otworzył "Dodaj opinię" (patrz addReviewBtn click listener niżej).
let mainFormHandle: ReturnType<typeof initCommentForm> | null = null;


function onVoteClicked(comment: Comment, newVote: Votetype, upvote_element: HTMLElement, downvote_element: HTMLElement) : void {

    const previous_vote = state.comments_up_down_votes[comment.id] || null;

    var upvote_delta : number = 0;
    var downvote_delta : number = 0;

    if (previous_vote == null){
        state.comments_up_down_votes[comment.id] = newVote;
        if (newVote == "upvote") upvote_delta = 1;
        else if (newVote == "downvote") downvote_delta = 1;
    }
    else if (previous_vote == newVote){
        delete state.comments_up_down_votes[comment.id];
        if (previous_vote == "downvote") downvote_delta = -1;
        else if (previous_vote == "upvote") upvote_delta = -1;
    }
    else if (previous_vote != newVote){
        state.comments_up_down_votes[comment.id] = newVote;
        if (previous_vote == "upvote"){
            upvote_delta = -1;
            downvote_delta = 1;
        }
        else if (previous_vote == "downvote"){
            upvote_delta = 1;
            downvote_delta = -1;
        }
    }

    saveVotesToStorage(state.comments_up_down_votes);

    if (downvote_delta == -1) downvote_element.classList.remove("active-vote");
    else if (downvote_delta == 1) downvote_element.classList.add("active-vote");

    if (upvote_delta == -1) upvote_element.classList.remove("active-vote");
    else if (upvote_delta == 1) upvote_element.classList.add("active-vote");

    upvote_element.setAttribute("aria-pressed", String(upvote_element.classList.contains("active-vote")));
    downvote_element.setAttribute("aria-pressed", String(downvote_element.classList.contains("active-vote")));

    api.submitVote(comment.id, upvote_delta, downvote_delta);

    const upVotesCountElement = upvote_element.querySelector(".upvote-count");
    upVotesCountElement!.textContent = (comment.upvotes + upvote_delta).toString();

    const downVotesCountElement = downvote_element.querySelector(".downvote-count");
    downVotesCountElement!.textContent = (comment.downvotes + downvote_delta).toString();

    comment.upvotes += upvote_delta;
    comment.downvotes += downvote_delta;
}

interface AppState {
    comments: Comment[];
    status: PageStatus;
    error: string;
    sortBy: CommentSortField;
    focusedIndex: number;
    post_id: number | null;
    comments_up_down_votes: { [commentId: number]: Votetype };
    visibleCount: number;
    subjects: Subject[];
    subjectsLoaded: boolean;
    activeCommentTarget: CommentTarget;
    collapsedTargets: Set<CommentTarget>;
}

// IntersectionObserver do śledzenia scrolla - używany tylko w trybie płaskim (nie przy grupowaniu wg przedmiotu)
const observer = new IntersectionObserver((entries) => {
    if (entries[0].isIntersecting) {
        state.visibleCount += 10;
        render();
    }
}, { rootMargin: "100px" });

// Otwiera dany formularz (główny/ogólny/przedmiotowy) i zamyka wszystkie pozostałe - tylko
// jeden jest widoczny naraz. Kliknięcie już aktywnego przycisku zamyka go z powrotem do głównego.
function setActiveCommentTarget(target: CommentTarget): void {
    state.activeCommentTarget = state.activeCommentTarget === target ? "main" : target;
    render();
}

// Główny formularz jest domyślnie schowany - otwiera go przycisk "Dodaj opinię" w nagłówku.
// Budujemy go raz, gdy przedmioty/lata się załadują (jego widoczność steruje osobna klasa,
// żeby nie trzeba było przebudowywać formularza przy każdym otwarciu/zamknięciu).
function renderMainCommentArea(): void {
    // Czekamy też na state.status === "success" (komentarze), nie tylko na przedmioty - inaczej
    // przy wyjątkowo niefortunnym czasowaniu dwóch równoległych zapytań (loadComments/loadSubjects
    // lecą razem, bez ustalonej kolejności zakończenia) wyszukiwarka przedmiotu w formularzu
    // mogłaby zbudować się z pustym state.comments i posortować się alfabetycznie zamiast wg
    // aktywności (patrz sortSubjectsByRecentActivity) - formularz buduje się raz, więc to by się
    // już nie naprawiło samo do końca sesji na tej stronie.
    if (!state.subjectsLoaded || state.status !== "success" || !state.post_id || mainFormRendered) return;
    mainFormRendered = true;

    mainFormHandle = initCommentForm(state.post_id, commentForm, addCommentToList, {
        subjects: sortSubjectsByRecentActivity(state.subjects, state.comments),
        onSubmitted: () => { if (state.post_id) markPostAsReviewed(state.post_id); applyAlreadyReviewedButtonState(); },
        onAutoClose: () => closeAddReviewDialog(),
        onInstantSuccess: (comment) => { closeAddReviewDialog(); renderThanksBanner(comment.status); },
        onOptimisticClose: () => closeAddReviewDialog(),
        onReopen: () => openAddReviewDialog(),
        onCommentConfirmed: confirmComment,
        onCommentRemoved: removeCommentFromList,
        facultyCandidates,
        universitySlug: universitySlugForPicker,
        preselectFacultySlug,
        supportsExamDimensions: !!dimensionTabContainer,
    });
}

// Widok zgrupowany wg przedmiotu: "Komentarz ogólny" tylko gdy ma jakiś komentarz (dodawanie
// komentarza ogólnego jest i tak zawsze możliwe przez główny formularz na górze strony),
// potem sekcje przedmiotów wg tego, gdzie pojawił się najnowszy komentarz - każda z nagłówkiem i przyciskiem "+".
function renderGroupedView(comments: Comment[]): void {
    const groups = groupCommentsBySubject(comments)
        .filter(group => group.subject !== null || group.comments.length > 0);

    groups.forEach(group => {
        const identity: CommentTarget = group.subject ? group.subject.id : "general";
        // Zarówno "Dodaj komentarz do tego przedmiotu", jak i "Dodaj komentarz ogólny" są na
        // razie wyłączone tą samą flagą (patrz SUBJECT_QUICK_ADD_ENABLED) - dodawanie opinii
        // (w tym ogólnych) wciąż jest możliwe przez główny formularz na górze strony.
        const canQuickAdd = SUBJECT_QUICK_ADD_ENABLED;
        const isActive = canQuickAdd && state.activeCommentTarget === identity;
        const isCollapsed = state.collapsedTargets.has(identity);
        const heading_text = group.subject ? group.subject.name : GENERAL_SUBJECT_LABEL;
        const addBtnLabel = group.subject ? "Dodaj komentarz do tego przedmiotu" : "Dodaj komentarz ogólny";

        const section = document.createElement("section");
        section.className = isCollapsed ? "comment-group comment-group-collapsed" : "comment-group";

        const header = document.createElement("div");
        header.className = "comment-group-header";

        const list = document.createElement("div");
        list.className = "comment-group-list";

        const titleWrap = document.createElement("div");
        titleWrap.className = "comment-group-title";

        const collapseBtn = document.createElement("button");
        collapseBtn.type = "button";
        collapseBtn.className = "comment-group-collapse-btn";
        collapseBtn.setAttribute("aria-expanded", String(!isCollapsed));
        collapseBtn.setAttribute("aria-label", `${isCollapsed ? "Rozwiń" : "Zwiń"} sekcję: ${heading_text}`);
        collapseBtn.addEventListener("click", () => {
            if (state.collapsedTargets.has(identity)) {
                state.collapsedTargets.delete(identity);
            } else {
                state.collapsedTargets.add(identity);
                // Zwinięcie sekcji z otwartym formularzem "+" - dezaktywuj go razem z sekcją.
                if (state.activeCommentTarget === identity) {
                    state.activeCommentTarget = "main";
                }
            }
            render();
        });
        titleWrap.appendChild(collapseBtn);

        const heading = document.createElement("h3");
        heading.textContent = heading_text;
        titleWrap.appendChild(heading);

        header.appendChild(titleWrap);

        const quickForm = document.createElement("div");
        quickForm.className = "comment-group-quick-form"; // klasa comment-form-box (styl pola) doklejana dopiero po otwarciu formularza
        quickForm.setAttribute("aria-label", addBtnLabel);

        if (canQuickAdd) {
            const addBtn = document.createElement("button");
            addBtn.type = "button";
            addBtn.className = isActive ? "add-comment-btn active" : "add-comment-btn";
            addBtn.setAttribute("aria-pressed", String(isActive));
            addBtn.innerHTML = `<span class="add-comment-btn-icon" aria-hidden="true">+</span> ${addBtnLabel}`;
            addBtn.addEventListener("click", () => setActiveCommentTarget(identity));
            header.appendChild(addBtn);
        }

        section.append(header, quickForm);

        if (isActive) {
            quickForm.classList.add("comment-form-box");
            initCommentForm(state.post_id!, quickForm, addCommentToList, {
                subjects: state.subjects,
                fixedSubject: group.subject,
                onSubmitted: () => { if (state.post_id) markPostAsReviewed(state.post_id); applyAlreadyReviewedButtonState(); },
                onCommentConfirmed: confirmComment,
                onCommentRemoved: removeCommentFromList,
                facultyCandidates,
                universitySlug: universitySlugForPicker,
                preselectFacultySlug,
                supportsExamDimensions: !!dimensionTabContainer,
            });
        }

        if (group.comments.length === 0) {
            const empty = document.createElement("p");
            empty.className = "comment-group-empty";
            empty.textContent = "Brak komentarzy w tej kategorii.";
            list.appendChild(empty);
        } else {
            sortComments(group.comments, "newest").forEach((comment) => {
                const currentUserVote = state.comments_up_down_votes?.[comment.id] ?? null;
                const article = renderComment(comment, currentUserVote, onVoteClicked, { showSubjectBadge: false, dimension: commentDimension() });
                list.appendChild(article);
            });
        }

        section.appendChild(list);
        commentList.appendChild(section);
    });
}

// Filtruje do opinii z FAKTYCZNIE wypełnionym danym wymiarem - patrz notka przy interfejsie
// Comment (comments.ts): rating jest null tylko dla opinii egzamin-only z importu wpia-wiki.
// Zakładka "Egzamin" MUSI liczyć się po KAŻDYM wymiarze egzaminu (rating/atmosfera/trudność/
// poleca), NIE tylko exam_rating - część opinii z importu wpia-wiki ocenia egzamin innym
// wymiarem (np. samo exam_would_take_again) bez exam_rating - filtrowanie tylko po exam_rating
// chowałoby więc te opinie CAŁKOWICIE z zakładki (zgłoszenie: "opinie o egzaminie się nie
// pokazują" mimo realnego "Opinie: 17" w podsumowaniu, które liczy się tak samo jak tu - patrz
// recalculate_stats() w models.py, ten sam warunek). Poza WPiA (dimensionTabContainer === null)
// ta funkcja i tak nigdy nie jest wołana z czymkolwiek innym niż "zajecia" - patrz render() niżej.
// Zakładka, na której renderują się karty opinii - przekazywana do renderComment, żeby opinia
// oceniająca OBA wymiary pokazywała na każdej zakładce tylko swoją część (oceny i treść), a nie
// obie "pod sobą" (na wyraźną prośbę 2026-10-15). Poza WPiA (brak zakładek) - undefined, czyli
// dotychczasowy widok całości.
function commentDimension(): "zajecia" | "egzamin" | undefined {
    if (!dimensionTabContainer) return undefined;
    return activeDimension === "egzamin" ? "egzamin" : "zajecia";
}

function filterCommentsByDimension(comments: Comment[], dimension: DimensionTab): Comment[] {
    if (dimension === "egzamin") {
        return comments.filter(c =>
            c.exam_rating !== null || c.exam_atmosphere !== null
            || c.exam_difficulty !== null || c.exam_would_take_again !== null
        );
    }
    return comments.filter(c => c.rating !== null);
}

function render(): void {
    observer.disconnect(); // Odłączamy obserwatora przy każdym nowym renderze
    renderMainCommentArea();

    if (activeDimension === "pytania") {
        renderExamQuestionsPanel();
        return;
    }

    commentList.innerHTML = "";

    if (state.status === "loading") {
        statusBar.textContent = "Ładowanie";
        return;
    }

    if (state.status === "error") {
        statusBar.textContent = `Błąd: ${state.error}`;
        return;
    }

    const dimensionComments = dimensionTabContainer ? filterCommentsByDimension(state.comments, activeDimension) : state.comments;

    if (dimensionComments.length === 0) {
        const empty = document.createElement("p");
        empty.className = "comments-empty";
        empty.textContent = activeDimension === "egzamin"
            ? "Nikt jeszcze nie ocenił egzaminu u tej osoby. Napisz pierwszą opinię!"
            : "Nikt jeszcze nie dodał opinii o tym wykładowcy. Napisz pierwszą i pomóż innym studentom podjąć decyzję!";
        commentList.appendChild(empty);
        statusBar.textContent = "";
        return;
    }

    if (state.sortBy === "by_subject") {
        renderGroupedView(dimensionComments);
        statusBar.textContent = "";
        return;
    }

    // Tryb płaski (pozostałe metody sortowania) - dokładnie jak dotychczas, bez nagłówków przedmiotów.
    const sorted = sortComments(dimensionComments, state.sortBy);
    const visibleComments = sorted.slice(0, state.visibleCount);

    visibleComments.forEach((comment) => {
        const currentUserVote = state.comments_up_down_votes?.[comment.id] ?? null;
        const article = renderComment(comment, currentUserVote, onVoteClicked, { dimension: commentDimension() });
        commentList.appendChild(article);
    });

    if (state.visibleCount < sorted.length) {
        const sentinel = document.createElement("div");
        sentinel.id = "comment-scroll-sentinel";
        sentinel.style.height = "20px";
        commentList.appendChild(sentinel);
        observer.observe(sentinel);
    }

    statusBar.textContent = "";
}

async function loadComments(): Promise<void> {
    state = { ...state, status: "loading" };
    render();

    const res = await api.getComments(state.post_id!);
    if (!res.ok) {
        state = { ...state, status: "error", error: "Nie znaleziono komentarzy" };
        render();
        return;
    }

    state = { ...state, comments: res.data.comments, status: "success", visibleCount: 10 };
    render();
}

// Pobiera listę przedmiotów - potrzebna zarówno do głównego formularza (wyszukiwarka
// przedmiotu), jak i do formularzy "+" przy nagłówkach. Ograniczona do wydziału TEGO
// wykładowcy (patrz facultyId niżej) - inaczej wyszukiwarka pokazywałaby przedmioty ze
// wszystkich wydziałów wszystkich uczelni naraz.
async function loadSubjects(): Promise<void> {
    const facultyId = postInformation?.dataset.postFacultyId
        ? Number(postInformation.dataset.postFacultyId)
        : undefined;
    const res = await api.getSubjects(facultyId);

    if (res.ok) {
        state = {
            ...state,
            subjects: res.data.subjects,
            subjectsLoaded: true,
        };
    } else {
        commentForm.textContent = "Nie udało się załadować formularza komentarzy. Odśwież stronę.";
        return;
    }

    render();
}

// Optimistic UI (patrz CommentFormOptions.onCommentConfirmed/onCommentRemoved w comments.ts) -
// rozbite na trzy kroki zamiast jednej funkcji "dodaj i policz naraz", którą był dawniej
// handleNewComment: (1) dodanie do listy dzieje się OD RAZU, optymistycznie, z tymczasowym
// (ujemnym) id, zanim serwer w ogóle odpowie; (2) liczniki/średnie naliczają się dopiero przy
// potwierdzeniu - inaczej policzyłyby się dla KAŻDEJ próby wysłania, także tych, które serwer
// potem wstrzyma do moderacji albo odrzuci; (3) cofnięcie usuwa optymistyczny wpis, jeśli
// serwer jednak nie zatwierdził publikacji od razu - nic nie trzeba odliczać, bo nic nie zostało
// jeszcze doliczone.
function addCommentToList(newComment: Comment) {
    state = {
        ...state,
        comments: [...state.comments, newComment],
        visibleCount: state.visibleCount + 1 // Upewniamy się, że nowy wpis wyświetli się od razu
    };
    render();
}

function removeCommentFromList(tempId: number) {
    state = {
        ...state,
        comments: state.comments.filter(c => c.id !== tempId),
        visibleCount: Math.max(0, state.visibleCount - 1),
    };
    render();
}

// Podmienia tymczasowy (optymistyczny) wpis na prawdziwy z serwera - w MIEJSCU (ta sama pozycja
// w liście, bez przeskakiwania na koniec) - i DOPIERO TERAZ nalicza liczniki/średnie, patrz
// notka wyżej.
function confirmComment(tempId: number, realComment: Comment) {
    state = {
        ...state,
        comments: state.comments.map(c => c.id === tempId ? realComment : c),
    };

    // Formularz na żywo wymaga oceny zajęć zawsze (patrz initCommentForm w comments.ts) - rating
    // jest tu więc w praktyce zawsze liczbą, nigdy null (null trafia się tylko w opiniach
    // zaimportowanych z wpia-wiki.pl, egzamin-only - te nie przechodzą przez ten formularz).
    // Guard mimo to, żeby nie liczyć na tę niezmienność w milczeniu.
    if (realComment.rating !== null) {
        number_of_comments++;
        avg_rating = (number_of_comments - 1) / number_of_comments * avg_rating + 1 / number_of_comments * realComment.rating;
        ratingDistribution[String(realComment.rating)] = (ratingDistribution[String(realComment.rating)] ?? 0) + 1;

        if (realComment.would_take_again !== null) {
            wouldTakeAgainAnswered++;
            if (realComment.would_take_again) wouldTakeAgainYes++;
        }

        ratingSummaryContainer.replaceChildren();
        ratingSummaryContainer.appendChild(
            renderRatingSummary(avg_rating, number_of_comments, ratingDistribution, computeWouldTakeAgainPercent(), avgClassDifficulty)
        );
    }

    // Druga, opcjonalna ocena - egzamin (patrz supports_exam_dimensions/CommentFormOptions.
    // supportsExamDimensions) - śledzona osobno, ta sama formuła średniej kroczącej co wyżej,
    // ale po exam_rating, niezależnie od tego, czy TA SAMA opinia oceniła też zajęcia. Procent
    // "poleca egzamin" celowo NIE jest tu doliczany na bieżąco (backend nie eksponuje surowych
    // liczników yes/answered dla egzaminu, tylko już gotowy procent - patrz data-post-exam-would-
    // take-again-percent w post_detail.html) - zostaje statyczny do następnego przeładowania
    // strony, ten sam kompromis co przy wielu innych drobnych licznikach w serwisie.
    if (realComment.exam_rating !== null) {
        examNumberOfComments++;
        avgExamRating = (examNumberOfComments - 1) / examNumberOfComments * avgExamRating
            + 1 / examNumberOfComments * realComment.exam_rating;
        examRatingDistribution[String(realComment.exam_rating)] =
            (examRatingDistribution[String(realComment.exam_rating)] ?? 0) + 1;

        renderExamSummaryIfActive();
    }

    render();
}



function loadJsonScriptData(elementId: string): { [key: string]: number } {
    const el = document.getElementById(elementId);
    if (!el || !el.textContent) return {};
    try {
        return JSON.parse(el.textContent);
    } catch {
        return {};
    }
}

function loadRatingDistribution(): { [key: string]: number } {
    return loadJsonScriptData("rating-distribution-data");
}

function computeWouldTakeAgainPercent(): number | null {
    return wouldTakeAgainAnswered > 0 ? Math.round(100 * wouldTakeAgainYes / wouldTakeAgainAnswered) : null;
}

// ===== Podział zajęcia/egzamin (patrz DimensionTab wyżej) - tylko gdy dimensionTabContainer
// istnieje w DOM (patrz supports_exam_dimensions w views.post_detail/post_detail.html) =====

// Podsumowanie na zakładce "Egzamin" - ten sam widget gwiazdkowy co zajęcia (renderRatingSummary
// w posts.ts), tylko osobny wymiar (Comment.exam_rating, EXAM_RATING_LABELS zamiast
// STAR_RATING_LABELS) i budowane ręcznie, nie przez renderRatingSummary wprost - bo to jedyne
// miejsce w serwisie z DWOMA niezależnymi rozkładami ocen na tej samej stronie.
function renderExamSummaryContent(): HTMLElement {
    const summary = document.createElement("div");
    summary.className = "rating-summary";

    const bars = renderRatingDistribution(examRatingDistribution, { showLabels: true, labels: EXAM_RATING_LABELS });
    bars.classList.add("rating-summary-bars");

    const headline = document.createElement("div");
    headline.className = "rating-summary-headline";

    // avgExamRating===0 jest jednoznaczne z "nikt jeszcze nie ocenił egzaminu gwiazdkami" -
    // prawdziwa średnia skali 1-5 nigdy nie wynosi dokładnie 0 (patrz data-post-avg-exam-rating w
    // post_detail.html: puste/null z backendu -> "" -> Number("" || 0) -> 0 tutaj). Odróżniamy to
    // od "0,0" + puste gwiazdki (co wygląda jak realna, najniższa ocena) - część opinii o
    // egzaminie ocenia go innym wymiarem (exam_atmosphere/exam_would_take_again z importu) bez
    // exam_rating, więc ten przypadek jest możliwy, nie tylko teoretyczny (zgłoszenie: "są oceny,
    // ale ich nie ma o egzaminie" - mylące "0,0" mimo realnych opinii, ten sam problem co wcześniej
    // przy trudności).
    if (avgExamRating === 0 && examNumberOfComments > 0) {
        const noRatingMsg = document.createElement("div");
        noRatingMsg.className = "rating-summary-number-placeholder";
        noRatingMsg.textContent = "Brak ocen";
        headline.appendChild(noRatingMsg);
    } else {
        const number = document.createElement("div");
        number.className = "rating-summary-number";
        number.textContent = avgExamRating.toFixed(1).replace(".", ",");

        const icons = showPartialStars(avgExamRating);
        icons.classList.add("rating-summary-stars");

        headline.append(number, icons);
    }

    // Bez warunku "brak jeszcze opinii" - ten sam, zawsze-ten-sam format co "Opinie: N" dla
    // zajęć (renderRatingSummary w posts.ts), łącznie z N=0 (na wyraźną prośbę o spójność).
    const count = document.createElement("div");
    count.className = "rating-summary-count";
    count.textContent = `Opinie: ${examNumberOfComments}`;
    headline.appendChild(count);

    const badge = renderWouldTakeAgainBadge(examWouldTakeAgainPercentFromServer);
    if (badge) {
        badge.classList.add("rating-summary-would-take-again");
        headline.appendChild(badge);
    }

    if (avgExamDifficulty !== null) {
        const difficultyBadge = renderEaseOfPassingText(avgExamDifficulty);
        if (difficultyBadge) {
            difficultyBadge.classList.add("rating-summary-difficulty");
            headline.appendChild(difficultyBadge);
        }
    }

    summary.append(headline, bars);
    return summary;
}

function renderExamSummaryIfActive(): void {
    if (activeDimension !== "egzamin") return;
    ratingSummaryContainer.replaceChildren();
    ratingSummaryContainer.appendChild(renderExamSummaryContent());
}

let examQuestionsLoaded = false;
let examQuestionsList: ExamQuestion[] = [];
let examQuestionsFormRendered = false;
// Referencja do kontenera formularza - budowany raz (patrz renderExamQuestionsPanel), chowany/
// pokazywany przyciskiem "Dodaj pytanie" (patrz listener niżej), NIE zawsze widoczny - tak samo
// jak główny formularz opinii (#comment-form/.comment-form-open), żeby zakładka "Pytania na
// egzaminie" domyślnie pokazywała samą listę, bez od razu rozwiniętego formularza.
let examQuestionFormContainer: HTMLDivElement | null = null;

async function loadExamQuestionsIfNeeded(): Promise<void> {
    if (examQuestionsLoaded || !state.post_id) return;
    examQuestionsLoaded = true;

    const res = await api.getExamQuestions(state.post_id);
    if (res.ok) {
        examQuestionsList = res.data.questions;
    }
    if (activeDimension === "pytania") renderExamQuestionsList();
}

function renderExamQuestionsList(): void {
    if (!examQuestionsArea || !state.post_id) return;
    const postId = state.post_id;
    // Usuwamy tylko listę/placeholder - formularz (jeśli już zbudowany) zostaje na miejscu,
    // żeby nie tracić jego stanu (otwarty/zamknięty, wpisana treść).
    Array.from(examQuestionsArea.children).forEach(child => {
        if (child !== examQuestionFormContainer) child.remove();
    });

    if (examQuestionsList.length === 0) {
        const empty = document.createElement("p");
        empty.className = "comments-empty";
        empty.textContent = examQuestionsLoaded
            ? "Nikt jeszcze nie dodał tu pytania egzaminacyjnego. Dodaj pierwsze!"
            : "Ładowanie...";
        examQuestionsArea.appendChild(empty);
        return;
    }

    // Jedna odznaka źródła na CAŁĄ listę (nie przy każdym pytaniu z osobna, jak wcześniej) - na
    // wyraźną prośbę, żeby "W" nie powtarzało się przy każdej linii. Stawiana raz na górze, gdy
    // choć jedno pytanie pochodzi z importu wpia-wiki (patrz renderCommentCorner w comments.ts -
    // tam per-opinia ma sens, bo każda opinia to osobna karta; tu pytania to zwykłe linie).
    if (examQuestionsList.some(q => q.is_from_wpia_wiki)) {
        const sourceRow = document.createElement("div");
        sourceRow.className = "exam-questions-source-row";
        const label = document.createElement("span");
        label.textContent = "Część pytań pochodzi z:";
        sourceRow.appendChild(label);
        sourceRow.appendChild(renderWpiaWikiSourceBadge());
        examQuestionsArea.appendChild(sourceRow);
    }

    examQuestionsList.forEach(question => {
        examQuestionsArea!.appendChild(renderExamQuestion(question, postId));
    });
}

function renderExamQuestionsPanel(): void {
    if (!examQuestionsArea || !state.post_id) return;
    loadExamQuestionsIfNeeded();

    if (!examQuestionsFormRendered) {
        examQuestionsFormRendered = true;
        examQuestionFormContainer = document.createElement("div");
        examQuestionFormContainer.id = "exam-question-form";
        examQuestionFormContainer.className = "comment-form-box";
        examQuestionFormContainer.style.display = "none";
        initExamQuestionForm(state.post_id, examQuestionFormContainer, (newQuestions) => {
            // Na końcu listy (nie na początku) - na wyraźną prośbę.
            examQuestionsList = [...examQuestionsList, ...newQuestions];
            renderExamQuestionsList();
        }, {
            onAutoClose: () => {
                if (examQuestionFormContainer) examQuestionFormContainer.style.display = "none";
                addExamQuestionBtn?.setAttribute("aria-expanded", "false");
                addExamQuestionBtn?.classList.remove("active");
            },
        });
        examQuestionsArea.appendChild(examQuestionFormContainer);
    }

    renderExamQuestionsList();
}

// Płynne pojawianie/znikanie rozkładu (paski + nagłówek) przy przełączaniu zakładek (na wyraźną
// prośbę - wcześniej podmiana była błyskawiczna, jednoklatkowa) - krótki fade-out, podmiana
// zawartości POD SPODEM (użytkownik jej wtedy nie widzi, bo opacity:0), potem fade-in nowej
// zawartości. requestAnimationFrame przed zdjęciem klasy fade-out - inaczej przeglądarka mogłaby
// zarejestrować nową zawartość i usunięcie klasy w tej samej klatce, przez co transition by się
// nie odpalił (brak zmiany stanu do animowania).
const RATING_SUMMARY_FADE_MS = 160;

function setRatingSummaryContent(buildContent: () => HTMLElement | null): void {
    ratingSummaryContainer.classList.add("rating-summary-fade-out");
    window.setTimeout(() => {
        const content = buildContent();
        ratingSummaryContainer.replaceChildren(...(content ? [content] : []));
        requestAnimationFrame(() => {
            ratingSummaryContainer.classList.remove("rating-summary-fade-out");
        });
    }, RATING_SUMMARY_FADE_MS);
}

function updateDimensionView(): void {
    const isPytania = activeDimension === "pytania";
    commentList.hidden = isPytania;
    commentForm.hidden = isPytania;
    statusBar.hidden = isPytania;
    // style.display (NIE .hidden) - .add-review-btn ma w CSS jawne "display: inline-flex", które
    // (ta sama specyficzność co atrybutowy selektor [hidden], późniejsza reguła wygrywa) bije
    // atrybut hidden i faktycznie nie chowa przycisku - znaleziony realnie na żywym serwerze
    // (zgłoszenie: "w zakładce Pytania nadal dodaje się opinię, nie pytanie" - przycisk "Dodaj
    // opinię" zostawał widoczny/klikalny mimo hidden=true).
    addReviewBtn.style.display = isPytania ? "none" : "";
    addReviewHint.hidden = isPytania;
    if (addExamQuestionBtn) addExamQuestionBtn.style.display = isPytania ? "" : "none";
    if (examQuestionsArea) examQuestionsArea.hidden = !isPytania;

    if (activeDimension === "egzamin") {
        setRatingSummaryContent(renderExamSummaryContent);
    } else if (activeDimension === "zajecia") {
        setRatingSummaryContent(() =>
            renderRatingSummary(avg_rating, number_of_comments, ratingDistribution, computeWouldTakeAgainPercent(), avgClassDifficulty)
        );
    } else {
        // "pytania" - na wyraźną prośbę żaden rozkład/podsumowanie NIE pokazuje się na tej
        // zakładce (dotyczy ocen, nie pytań). #rating-summary (górny, wspólny kontener) trzeba
        // tu jawnie wyczyścić - inaczej zostaje w nim STARA zawartość z poprzednio aktywnej
        // zakładki (Zajęcia/Egzamin), co realnie wystąpiło na żywym serwerze jako zdublowany,
        // nieaktualny rozkład na górze strony.
        setRatingSummaryContent(() => null);
    }

    state.visibleCount = 10;
    render();
}

addExamQuestionBtn?.addEventListener("click", () => {
    if (!examQuestionFormContainer) return;
    const isOpen = examQuestionFormContainer.style.display !== "none";
    examQuestionFormContainer.style.display = isOpen ? "none" : "block";
    addExamQuestionBtn.setAttribute("aria-expanded", String(!isOpen));
    addExamQuestionBtn.classList.toggle("active", !isOpen);
});

function renderDimensionTabs(): void {
    if (!dimensionTabContainer) return;

    const TABS: { value: DimensionTab, label: string }[] = [
        { value: "zajecia", label: "Zajęcia" },
        { value: "egzamin", label: "Egzamin" },
        { value: "pytania", label: "Pytania na egzaminie" },
    ];

    const nav = document.createElement("div");
    nav.className = "dimension-tabs";
    nav.setAttribute("role", "tablist");

    const buttons = TABS.map(tab => {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "dimension-tab-btn";
        btn.textContent = tab.label;
        btn.setAttribute("role", "tab");
        btn.setAttribute("aria-selected", String(tab.value === activeDimension));
        btn.classList.toggle("active", tab.value === activeDimension);
        btn.addEventListener("click", () => {
            if (activeDimension === tab.value) return;
            activeDimension = tab.value;
            buttons.forEach((b, i) => {
                b.classList.toggle("active", TABS[i].value === tab.value);
                b.setAttribute("aria-selected", String(TABS[i].value === tab.value));
            });
            updateDimensionView();
        });
        nav.appendChild(btn);
        return btn;
    });

    dimensionTabContainer.appendChild(nav);
}

function loadCandidateFaculties(): FacultyCandidate[] {
    const el = document.getElementById("candidate-faculties-data");
    if (!el || !el.textContent) return [];
    try {
        return JSON.parse(el.textContent);
    } catch {
        return [];
    }
}

const postInformation = document.getElementById("post-info");
state.post_id = postInformation ? Number(postInformation.dataset.postId) : null;
let number_of_comments = postInformation ? Number(postInformation.dataset.postNumberOfComments) : 0;
let avg_rating = postInformation ? Number(postInformation.dataset.postAvgRating) : 0;
let wouldTakeAgainYes = postInformation ? Number(postInformation.dataset.postWouldTakeAgainYes) : 0;
let wouldTakeAgainAnswered = postInformation ? Number(postInformation.dataset.postWouldTakeAgainAnswered) : 0;
let ratingDistribution = loadRatingDistribution();

// Podział zajęcia/egzamin (patrz DimensionTab wyżej) - tylko WPiA UW na razie (dimensionTabContainer
// istnieje w DOM wyłącznie gdy supports_exam_dimensions=True, patrz post_detail.html).
let activeDimension: DimensionTab = "zajecia";
let avgExamRating = postInformation ? Number(postInformation.dataset.postAvgExamRating || 0) : 0;
let examNumberOfComments = postInformation ? Number(postInformation.dataset.postNumberOfExamComments) : 0;
const examWouldTakeAgainPercentFromServer = postInformation?.dataset.postExamWouldTakeAgainPercent
    ? Number(postInformation.dataset.postExamWouldTakeAgainPercent) : null;
let examRatingDistribution = loadJsonScriptData("exam-rating-distribution-data");
// Trudność zajęć/egzaminu (patrz renderEaseOfPassingText w generics.ts) - w odróżnieniu od avg_rating/
// avgExamRating wyżej CELOWO statyczna (nie przeliczana na bieżąco w confirmComment po nowym
// komentarzu) - Post nie ma osobnego licznika "ile opinii oceniło trudność" (w odróżnieniu od
// number_of_comments/number_of_exam_comments), więc nie da się tu policzyć poprawnej średniej
// kroczącej bez dodatkowego zapytania. Ten sam, już zaakceptowany kompromis co przy
// exam_would_take_again_percent wyżej (patrz notka przy confirmComment) - wartość odświeży się
// przy kolejnym przeładowaniu strony.
const avgClassDifficulty = postInformation?.dataset.postAvgClassDifficulty
    ? Number(postInformation.dataset.postAvgClassDifficulty) : null;
const avgExamDifficulty = postInformation?.dataset.postAvgExamDifficulty
    ? Number(postInformation.dataset.postAvgExamDifficulty) : null;

// Picker wydziału w formularzu komentarza (patrz renderFacultyPicker w comments.ts) - pokazuje
// się TYLKO gdy backend uznał wydział tej osoby za niejednoznaczny/nierozstrzygnięty (patrz
// needs_faculty_confirmation w views.post_detail), nie dla zwykłego, już potwierdzonego wydziału.
const needsFacultyConfirmation = postInformation?.dataset.needsFacultyConfirmation === "1";
const facultyCandidates: FacultyCandidate[] | undefined = needsFacultyConfirmation ? loadCandidateFaculties() : undefined;
const universitySlugForPicker = postInformation?.dataset.universitySlug || undefined;
// Sygnał z nawigacji (patrz ?wydzial= dopisywane do linków profili na liście wydziału - posts.ts/
// browser.ts) - z jakiego wydziału user trafił na ten profil. Gdy pasuje do jednego z kandydatów,
// picker zaznacza go od razu (patrz preselectSlug w renderFacultyPicker) - samo wysłanie opinii
// bez dotykania pola wystarczy, żeby to potwierdzić.
const preselectFacultySlug = new URLSearchParams(window.location.search).get("wydzial");

if (state.post_id) {
    loadComments();
    loadSubjects(); // inicjalizuje też główny formularz (po pobraniu przedmiotów/lat)
    ratingSummaryContainer.appendChild(
        renderRatingSummary(avg_rating, number_of_comments, ratingDistribution, computeWouldTakeAgainPercent(), avgClassDifficulty)
    );
    compareButtonContainer.appendChild(createCompareButton(state.post_id));
    renderDimensionTabs();
}
else throw Error("Nie znaleziono postu do załadowania");

recordPageView(state.post_id);

let hintTimeoutId: ReturnType<typeof setTimeout> | null = null;
const ALREADY_REVIEWED_HINT = "Możesz dodać tylko jedną opinię do tego profilu.";
const ALREADY_REVIEWED_BTN_TEXT = "✓ Dodano opinię";

// Krótkie okno wyciszenia PRAWIE natychmiast po udanym zapisie (patrz applyAlreadyReviewedButtonState
// niżej) - zamknięcie natywnego <dialog> potrafi samo z siebie odpalić "mouseenter"/"focus" na
// przycisku pod spodem (kursor "ląduje" na nim, gdy znika modal, który go zasłaniał - albo
// przeglądarka mimo blur() przed close() i tak przywraca fokus) - bez tego okna podpowiedź limitu
// pokazywała się sama, ułamek sekundy po dodaniu opinii, bez żadnej akcji użytkownika (zgłoszenie,
// ten sam wzorzec naprawiony analogicznie w kolo_naukowe_detail.ts).
let suppressHintUntil = 0;
const HINT_SUPPRESS_WINDOW_MS = 400;

function postAlreadyReviewed(): boolean {
    return !!(state.post_id && hasAlreadyReviewedThisPost(state.post_id));
}

function showAlreadyReviewedHint(): void {
    if (Date.now() < suppressHintUntil) return;
    if (hintTimeoutId) {
        clearTimeout(hintTimeoutId);
        hintTimeoutId = null;
    }
    addReviewHint.textContent = ALREADY_REVIEWED_HINT;
    addReviewHint.classList.add("visible");
}

function hideAlreadyReviewedHint(): void {
    if (hintTimeoutId) {
        clearTimeout(hintTimeoutId);
        hintTimeoutId = null;
    }
    addReviewHint.classList.remove("visible");
}

// Gdy user już dodał opinię do tego profilu (patrz hasAlreadyReviewedThisPost wyżej), przycisk
// zamienia się w informację "✓ Dodano opinię" - biały/neutralny (patrz
// .add-review-btn.limit-reached w CSS), zamiast zachęcającego pomarańczowego "Dodaj opinię" -
// kliknięcie w niego w tym stanie nic nie robi (patrz listener niżej), formularza i tak nie da
// się użyć drugi raz. Podpowiedź z treścią limitu pokazuje się dodatkowo na najechanie myszką.
// Wołane też z onSubmitted (patrz renderMainCommentArea/renderGroupedView niżej), żeby przycisk
// zaktualizował się od razu po udanym wysłaniu, bez czekania na przeładowanie strony.
function applyAlreadyReviewedButtonState(): void {
    if (postAlreadyReviewed()) {
        addReviewBtn.classList.add("limit-reached");
        addReviewBtn.textContent = ALREADY_REVIEWED_BTN_TEXT;
        suppressHintUntil = Date.now() + HINT_SUPPRESS_WINDOW_MS;
    }
}
applyAlreadyReviewedButtonState();

// Formularz opinii w osobnym oknie zamiast rozwijanej sekcji na stronie (patrz
// ADD_REVIEW_MODAL_ENABLED w generics.ts - dlaczego i jak wyłączyć) - #comment-form jest
// przenoszony do środka natywnego <dialog>, budowanego tu w JS, więc szablon HTML
// (post_detail.html) zostaje nietknięty i przy wyłączonej fladze strona zachowuje się dokładnie
// jak wcześniej (patrz addReviewBtn click listener niżej - gałąź "else" to stary kod).
if (ADD_REVIEW_MODAL_ENABLED) {
    addReviewDialog = document.createElement("dialog");
    addReviewDialog.className = "add-review-dialog";

    // Nazwisko wykładowcy w tytule ("Jan Kowalski - Dodaj opinię") zamiast osobnego podtytułu pod
    // spodem - modal zasłania nagłówek strony, więc bez tego nie było w nim żadnego śladu, KOGO
    // właśnie się ocenia. Imię i nazwisko NA POCZĄTKU (nie "Dodaj opinię - ...") - to jest
    // informacja, której user szuka wzrokiem najpierw ("czy to na pewno profil, na którym
    // byłem?") - że dodaje opinię, to już wie z samego faktu, że modal otworzył się po kliknięciu
    // tego przycisku. Czytane wprost z <h1> (patrz .lecturer-name-row w post_detail.html - zawiera
    // TYLKO imię i nazwisko, bez dodatkowego znacznika) zamiast dociągać post.name/surname osobno
    // - te pola i tak nie są nigdzie indziej trzymane w stanie JS tej strony.
    const dialogTitle = document.createElement("h2");
    dialogTitle.className = "add-review-dialog-title";
    const lecturerName = document.querySelector("h1")?.textContent?.trim() ?? "";
    dialogTitle.textContent = lecturerName ? `${lecturerName} - Dodaj opinię` : "Dodaj opinię";

    const closeBtn = document.createElement("button");
    closeBtn.type = "button";
    closeBtn.className = "add-review-dialog-close";
    closeBtn.setAttribute("aria-label", "Zamknij");
    closeBtn.innerHTML = "&times;";
    closeBtn.addEventListener("click", () => closeAddReviewDialog());

    // commentForm zajmuje swoje pierwotne miejsce w drzewie (patrz post_detail.html) - dialog
    // wskakuje na to miejsce, a sam formularz ląduje w środku niego.
    commentForm.replaceWith(addReviewDialog);
    addReviewDialog.append(dialogTitle, closeBtn, commentForm);

    // Klik w tło (::backdrop) dociera do JS jako klik na sam element <dialog> (bo trafia poza
    // wszystkich jego potomków) - to jedyny sposób odróżnienia go od kliku w treść formularza,
    // natywny <dialog> nie zamyka się na klik w tło sam z siebie.
    addReviewDialog.addEventListener("click", (event) => {
        if (event.target === addReviewDialog) closeAddReviewDialog();
    });

    // Jedno miejsce na "dialog się zamknął" niezależnie OD SPOSOBU zamknięcia (przycisk ×, klik w
    // tło, Escape - fires "cancel" -> domyślnie zamyka -> "close", albo programowe .close() z
    // onAutoClose w initCommentForm) - stan przycisku "Dodaj opinię" zawsze wraca do spójnego
    // stanu spoczynkowego.
    addReviewDialog.addEventListener("close", () => {
        addReviewBtn.setAttribute("aria-expanded", "false");
        addReviewBtn.classList.remove("active");
    });
}

addReviewBtn.addEventListener("mouseenter", () => {
    if (postAlreadyReviewed()) showAlreadyReviewedHint();
});
addReviewBtn.addEventListener("mouseleave", () => {
    if (postAlreadyReviewed()) hideAlreadyReviewedHint();
});
addReviewBtn.addEventListener("focus", () => {
    if (postAlreadyReviewed()) showAlreadyReviewedHint();
});
addReviewBtn.addEventListener("blur", () => {
    if (postAlreadyReviewed()) hideAlreadyReviewedHint();
});

addReviewBtn.addEventListener("click", () => {
    // Nic się nie dzieje - user już dodał opinię do tego profilu, nie ma czego otwierać (patrz
    // ALREADY_REVIEWED_BTN_TEXT wyżej - przycisk już to komunikuje samą swoją treścią).
    if (postAlreadyReviewed()) return;

    // Formularz jest zbudowany raz (patrz mainFormRendered), ale ma wiedzieć, na której zakładce
    // (Zajęcia/Egzamin) user aktualnie stoi - na "Egzamin" pola egzaminu mają być widoczne od
    // razu, a zajęcia (wciąż wymagane do wysyłki) chowają się za "Oceń od razu zajęcia" (patrz
    // setExamPrimary w comments.ts). Bez efektu poza WPiA (setExamPrimary jest wtedy no-opem).
    mainFormHandle?.setExamPrimary(activeDimension === "egzamin");

    if (addReviewDialog) {
        openAddReviewDialog();
        return;
    }

    // ADD_REVIEW_MODAL_ENABLED wyłączone - stare zachowanie, formularz rozwija się inline.
    const isOpen = commentForm.classList.toggle("comment-form-open");
    addReviewBtn.setAttribute("aria-expanded", String(isOpen));
    addReviewBtn.classList.toggle("active", isOpen);
});

// Zamiast przycisku "Wróć" - wyszukiwarka jak na stronie głównej, zawężona do WYDZIAŁU tego
// wykładowcy, nie całej uczelni (Enter przenosi na listę tego wydziału z wpisanym zapytaniem) -
// duża uczelnia (np. UW) ma dziesiątki wydziałów, więc samo university było zbyt szerokie i
// podpowiadało/wyszukiwało ludzi z zupełnie innych wydziałów tej samej uczelni.
postSearchForm.addEventListener("submit", (event) => {
    event.preventDefault();
    const query = postSearchInput.value.trim();
    const universitySlug = postSearchInfo.dataset.universitySlug;
    const facultySlug = postSearchInfo.dataset.facultySlug;
    const target = universitySlug
        ? (facultySlug ? `/${universitySlug}/${facultySlug}/` : `/${universitySlug}/`)
        : `/`;
    window.location.href = query ? `${target}?q=${encodeURIComponent(query)}` : target;
});

// Podpowiedzi w trakcie pisania (jak filtrowanie na bieżąco na stronie głównej) - tutaj nie ma
// listy do przefiltrowania w miejscu, więc pokazujemy rozwijaną listę pasujących wykładowców
// TEGO SAMEGO WYDZIAŁU, prowadzącą wprost na ich profile - university sam w sobie (bez faculty)
// jest za szeroki, patrz notka przy postSearchForm wyżej.
async function loadSearchCandidates(): Promise<void> {
    const universitySlug = postSearchInfo.dataset.universitySlug;
    const facultySlug = postSearchInfo.dataset.facultySlug;
    const res = await api.getPosts(universitySlug || undefined, facultySlug || undefined);
    if (res.ok) {
        searchCandidates = res.data.posts;
    }
}
loadSearchCandidates();

function matchesQuery(post: Post, normalizedQuery: string): boolean {
    const name = normalizeForSearch(post.name);
    const surname = normalizeForSearch(post.surname);
    return name.includes(normalizedQuery) ||
        surname.includes(normalizedQuery) ||
        `${name} ${surname}`.includes(normalizedQuery) ||
        `${surname} ${name}`.includes(normalizedQuery);
}

function renderSearchSuggestions(rawQuery: string): void {
    postSearchSuggestions.replaceChildren();

    const normalizedQuery = normalizeForSearch(rawQuery);
    if (!normalizedQuery) {
        postSearchSuggestions.classList.remove("open");
        return;
    }

    const matches = searchCandidates.filter(post => matchesQuery(post, normalizedQuery));

    // Podpowiedzi są zawężone do wydziału TEGO wykładowcy (patrz notka przy postSearchForm
    // wyżej) - ten sam wydział dopisujemy jako ?wydzial= (patrz profileHref w posts.ts), żeby
    // profil docelowej osoby wiedział, że wydział jest już znany z kontekstu, i nie pokazywał
    // zbędnego paska z wydziałem (patrz confirmed_faculty w views.post_detail).
    const facultySlug = postSearchInfo.dataset.facultySlug;
    matches.forEach(post => {
        const item = document.createElement("a");
        item.className = "search-suggestion-item";
        item.href = facultySlug ? `/${post.slug}?wydzial=${encodeURIComponent(facultySlug)}` : `/${post.slug}`;
        item.textContent = `${post.name} ${post.surname}`;
        postSearchSuggestions.appendChild(item);
    });

    // Bug: ta wyszukiwarka (w odróżnieniu od tej samej wyszukiwarki na liście wykładowców, patrz
    // add-lecturer-prompt w browser.ts) nigdy nie oferowała dodania osoby, gdy zapytanie nic nie
    // znalazło - jedyny sposób dodania kogoś z profilu innej osoby było ręczne wejście na listę
    // wydziału. Ten sam wpis "+ Dodaj ..." (identyczny styl - search-suggestion-create) co w
    // podpowiedziach imienia WEWNĄTRZ samego dialogu dodawania osoby (patrz renderSuggestions w
    // addLecturer.ts) - otwiera ten sam dialog, z zapytaniem jako podpowiedzią imienia/nazwiska.
    const trimmedQuery = rawQuery.trim();
    if (matches.length === 0 && trimmedQuery) {
        const universitySlug = postSearchInfo.dataset.universitySlug;
        const addOption = document.createElement("button");
        addOption.type = "button";
        addOption.className = "search-suggestion-item search-suggestion-create";
        addOption.textContent = `+ Dodaj „${trimmedQuery}”`;
        addOption.addEventListener("click", () => {
            postSearchSuggestions.classList.remove("open");
            openAddPersonDialog(universitySlug || null, facultySlug || null, trimmedQuery);
        });
        postSearchSuggestions.appendChild(addOption);
    }

    if (postSearchSuggestions.children.length > 0) {
        postSearchSuggestions.classList.add("open");
    } else {
        postSearchSuggestions.classList.remove("open");
    }
}

postSearchInput.addEventListener("input", () => {
    renderSearchSuggestions(postSearchInput.value);
});

postSearchInput.addEventListener("focus", () => {
    if (postSearchInput.value.trim()) {
        renderSearchSuggestions(postSearchInput.value);
    }
});

document.addEventListener("click", (event) => {
    if (!postSearchWrapper.contains(event.target as Node)) {
        postSearchSuggestions.classList.remove("open");
    }
});

attachDropdownKeyboardNav(postSearchInput, postSearchSuggestions);

// Wspólny baner podziękowania (patrz .add-person-thanks-banner w style.css - lewy zielony pasek,
// potwierdzenie sukcesu) - dwóch wołających: showJustAddedNotice niżej (na podstawie parametrów
// w URL, po przekierowaniu z popupu "Dodaj osobę") i onInstantSuccess w renderMainCommentArea
// (od razu po udanym wysłaniu opinii przez dialog "Dodaj opinię" na TYM SAMYM profilu, bez
// przeładowania strony). Dawniej (przed dialogiem - patrz ADD_REVIEW_MODAL_ENABLED) formularz
// komentarza był wprost na stronie, a potwierdzenie sukcesu pokazywało się w tym samym miejscu,
// w którym stał formularz - znikało razem z nim, bez niczego do zamykania. Teraz formularz siedzi
// w popupie, który nie ma gdzie pokazać "Dziękujemy" PO swoim zamknięciu - stąd ten sam baner co
// dla świeżo dodanej osoby (zgłoszenie: "biały popup co trzeba zamknąć" zamiast dawnego
// zielonego banera).
function renderThanksBanner(status: string, commentId?: number): void {
    const banner = document.createElement("div");
    banner.className = "add-person-thanks-banner";

    const text = document.createElement("p");
    text.textContent = status === "active"
        ? "Dziękujemy za podzielenie się opinią!"
        : "Dziękujemy! Twoja opinia zostanie opublikowana po sprawdzeniu przez moderację.";
    banner.appendChild(text);

    if (status !== "active" && commentId) {
        banner.appendChild(renderPostHocNotifyPrompt(
            [{ model: "comment", pk: commentId }], () => banner.remove()
        ));
    } else {
        window.setTimeout(() => banner.remove(), 5000);
    }

    document.querySelector("main")?.prepend(banner);
}

// Podziękowanie + status moderacji po dodaniu pierwszej opinii przez popup "Dodaj osobę" na
// liście (patrz onSubmitted w mountCommentForm w addLecturer.ts) - ten dialog przekierowuje tu od
// razu zamiast zostawiać na liście z osobnym banerem (TYLKO dla osoby, która już miała tu profil -
// świeżo utworzona osoba czeka na moderację i nie ma jeszcze publicznego profilu, więc dostaje
// baner tam, na liście, nie tutaj). Komunikat "Dziękujemy.../czeka na moderację" + widżet "zostaw
// e-mail" trzeba odtworzyć na miejscu, na podstawie parametrów w URL. Usuwane z URL zaraz po
// odczytaniu (history.replaceState) - inaczej odświeżenie/"Wstecz" na tę samą stronę pokazywałoby
// ten sam baner drugi raz.
function showJustAddedNotice(): void {
    const params = new URLSearchParams(window.location.search);
    const status = params.get("opinia_dodana");
    if (!status) return;

    const commentId = params.get("komentarz_id");

    params.delete("opinia_dodana");
    params.delete("komentarz_id");
    const rest = params.toString();
    history.replaceState(null, "", window.location.pathname + (rest ? `?${rest}` : ""));

    renderThanksBanner(status, commentId ? Number(commentId) : undefined);
}
showJustAddedNotice();

const sortMenu = renderSortMenu(COMMENT_SORT_OPTIONS, state.sortBy, (sortBy) => {
    saveCommentSort(sortBy);
    state = {
        ...state,
        sortBy,
        visibleCount: 10, // Reset scrolla po zmianie sortowania
        activeCommentTarget: "main", // Przy zmianie widoku zawsze wracamy do głównego formularza
    };
    render();
}, "Sortuj");
sortMenuContainer.appendChild(sortMenu.element);
